import React, { useEffect, useState } from 'react';
import { auditFetch, buildAuditHeaders } from '../utils/auditFetch';
import { mockUsers } from '../mockData';
import '../styles/Dashboard.css';

const referralTypeLabels = {
  interconsulta: 'Interconsulta',
  referencia: 'Referencia',
};

const priorityLabels = {
  rutina: 'Rutina',
  preferente: 'Preferente',
  urgente: 'Urgente',
};

const statusLabels = {
  solicitada: 'Solicitada',
  en_revision: 'En revision',
  aceptada: 'Aceptada',
  rechazada: 'Rechazada',
  completada: 'Completada',
  cancelada: 'Cancelada',
};

const specialties = [
  'Medicina General',
  'Ginecologia',
  'Pediatria',
  'Cardiologia',
  'Neurologia',
  'Traumatologia',
  'Dermatologia',
  'Psiquiatria',
  'Medicina Interna',
  'Emergencias',
];

const getDoctorOptionLabel = (targetDoctor) => `${targetDoctor.name} - ${targetDoctor.specialty}`;

const InterconsultationReferralPanel = ({
  appointmentId,
  patient,
  doctor,
  user,
  defaultDiagnosis = '',
  defaultClinicalSummary = '',
}) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const [referralType, setReferralType] = useState('interconsulta');
  const [sourceFacility, setSourceFacility] = useState('TeleMec');
  const [sourceService, setSourceService] = useState(doctor?.specialty || 'Medicina General');
  const [targetFacility, setTargetFacility] = useState('');
  const [targetService, setTargetService] = useState('');
  const [targetSpecialty, setTargetSpecialty] = useState('Medicina Interna');
  const [targetProviderId, setTargetProviderId] = useState('');
  const [targetProvider, setTargetProvider] = useState('');
  const [priority, setPriority] = useState('rutina');
  const [referenceJustified, setReferenceJustified] = useState(true);
  const [derivationJustified, setDerivationJustified] = useState(referralType === 'referencia');
  const [reverseFlow, setReverseFlow] = useState('contrareferencia');
  const [relatedDiagnosis, setRelatedDiagnosis] = useState(defaultDiagnosis);
  const [reason, setReason] = useState('');
  const [clinicalSummary, setClinicalSummary] = useState(defaultClinicalSummary);
  const [findingsAndTreatment, setFindingsAndTreatment] = useState('');
  const [requestedAction, setRequestedAction] = useState('');
  const [administrativeNotes, setAdministrativeNotes] = useState('');
  const [referrals, setReferrals] = useState([]);
  const [statusMessage, setStatusMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setRelatedDiagnosis((current) => current || defaultDiagnosis);
  }, [defaultDiagnosis]);

  useEffect(() => {
    setClinicalSummary((current) => current || defaultClinicalSummary);
  }, [defaultClinicalSummary]);

  const targetDoctors = mockUsers.doctors.filter((targetDoctor) => (
    targetDoctor.specialty === targetSpecialty
  ));

  const selectedTargetDoctor = mockUsers.doctors.find((targetDoctor) => targetDoctor.id === targetProviderId) || null;

  useEffect(() => {
    if (!appointmentId) return;
    auditFetch(`${backendUrl}/api/referrals/appointment/${appointmentId}`, {}, user)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('No se pudieron cargar referencias'))))
      .then((data) => setReferrals(Array.isArray(data.referrals) ? data.referrals : []))
      .catch((error) => {
        console.error('Error loading referrals:', error);
        setStatusMessage('No se pudieron cargar interconsultas previas.');
      });
  }, [appointmentId, backendUrl, user]);

  const openPdf = (referral) => {
    const url = `${backendUrl}/api/referrals/${referral.id}/pdf`;
    fetch(url, { headers: buildAuditHeaders(user) })
      .then((res) => {
        if (!res.ok) throw new Error('No se pudo generar el PDF');
        return res.blob();
      })
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        window.open(objectUrl, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
      })
      .catch((error) => {
        console.error('Error opening referral PDF:', error);
        setStatusMessage('No se pudo abrir el PDF HCU 053.');
      });
  };

  const saveReferral = async () => {
    if (!targetSpecialty || !reason.trim() || !clinicalSummary.trim()) {
      setStatusMessage('Completa especialidad destino, motivo y resumen clinico.');
      return;
    }

    setIsSaving(true);
    setStatusMessage('');
    try {
      const response = await auditFetch(`${backendUrl}/api/referrals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appointmentId,
          patient,
          doctor,
          referralType,
          targetSpecialty,
          targetProviderId,
          targetProvider,
          sourceFacility,
          sourceService,
          targetFacility,
          targetService,
          priority,
          referenceJustified,
          derivationJustified,
          reverseFlow,
          relatedDiagnosis,
          reason,
          clinicalSummary,
          findingsAndTreatment,
          requestedAction,
          administrativeNotes,
          sourceSpecialty: doctor?.specialty,
        }),
      }, user);
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'No se pudo guardar la referencia');
      setReferrals((current) => [data.referral, ...current]);
      setStatusMessage(`HCU 053 guardado. Codigo: ${data.referral.verificationCode}. Usa el boton PDF en el historial para imprimir o descargar.`);
    } catch (error) {
      console.error('Error saving referral:', error);
      setStatusMessage(error.message || 'No se pudo emitir la referencia.');
    } finally {
      setIsSaving(false);
    }
  };

  const updateReferralStatus = async (referral, status) => {
    const notes = window.prompt('Observacion del cambio de estado', '');
    if (notes === null) return;
    try {
      const response = await auditFetch(`${backendUrl}/api/referrals/${referral.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, notes }),
      }, user);
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'No se pudo actualizar el estado');
      setReferrals((current) => current.map((item) => (item.id === data.referral.id ? data.referral : item)));
      setStatusMessage(`Referencia ${statusLabels[status].toLowerCase()}.`);
    } catch (error) {
      console.error('Error updating referral status:', error);
      setStatusMessage(error.message || 'No se pudo actualizar la referencia.');
    }
  };

  return (
    <div className="digital-prescription-panel">
      <div className="clinical-files-header">
        <div>
          <h3>Interconsulta y referencia HCU 053</h3>
          <p>Derivacion a otra especialidad con motivo, prioridad, diagnostico, resumen y estado.</p>
        </div>
        <span>{referrals.length} ref.</span>
      </div>

      <div className="prescription-medication-card">
        <div className="prescription-grid">
          <select
            value={referralType}
            onChange={(event) => {
              setReferralType(event.target.value);
              setDerivationJustified(event.target.value === 'referencia');
            }}
          >
            <option value="interconsulta">Interconsulta</option>
            <option value="referencia">Referencia</option>
          </select>
          <select value={priority} onChange={(event) => setPriority(event.target.value)}>
            <option value="rutina">Rutina</option>
            <option value="preferente">Preferente</option>
            <option value="urgente">Urgente</option>
          </select>
          <select
            value={targetSpecialty}
            onChange={(event) => {
              setTargetSpecialty(event.target.value);
              setTargetProviderId('');
              setTargetProvider('');
              setTargetService(event.target.value);
            }}
          >
            {specialties.map((specialty) => (
              <option key={specialty} value={specialty}>{specialty}</option>
            ))}
          </select>
        </div>
        <div className="prescription-grid">
          <input value={sourceFacility} onChange={(event) => setSourceFacility(event.target.value)} placeholder="Establecimiento origen" />
          <input value={sourceService} onChange={(event) => setSourceService(event.target.value)} placeholder="Servicio origen" />
          <input value={targetFacility} onChange={(event) => setTargetFacility(event.target.value)} placeholder="Establecimiento destino" />
        </div>
                <div className="prescription-grid">
          <input value={targetService} onChange={(event) => setTargetService(event.target.value)} placeholder="Servicio destino" />
          <select
            value={targetProviderId}
            onChange={(event) => {
              const nextDoctor = mockUsers.doctors.find((item) => item.id === event.target.value);
              setTargetProviderId(nextDoctor?.id || '');
              setTargetProvider(nextDoctor ? getDoctorOptionLabel(nextDoctor) : '');
              if (nextDoctor?.specialty) setTargetService(nextDoctor.specialty);
            }}
          >
            <option value="">Medico receptor opcional</option>
            {targetDoctors.map((targetDoctor) => (
              <option key={targetDoctor.id} value={targetDoctor.id}>
                {targetDoctor.name} - {targetDoctor.nextAvailable || 'Agenda por confirmar'}
              </option>
            ))}
          </select>
          <input value={relatedDiagnosis} onChange={(event) => setRelatedDiagnosis(event.target.value)} placeholder="Diagnostico relacionado" />
        </div>
        <div className="referral-receiver-card">
          <div>
            <strong>Medico receptor</strong>
            <span>{selectedTargetDoctor ? getDoctorOptionLabel(selectedTargetDoctor) : 'Pendiente de asignar'}</span>
            <small>{selectedTargetDoctor ? `${selectedTargetDoctor.email || 'Sin correo'} · ${selectedTargetDoctor.nextAvailable || 'Agenda por confirmar'}` : 'La referencia se enviara a la especialidad destino.'}</small>
          </div>
          <div>
            <strong>Datos para revision</strong>
            <span>{targetSpecialty} · {priorityLabels[priority]}</span>
            <small>{requestedAction || 'Solicitud especifica pendiente'}</small>
          </div>
        </div>
        <div className="prescription-grid">
          <label className="referral-check-option">
            <input type="checkbox" checked={referenceJustified} onChange={(event) => setReferenceJustified(event.target.checked)} />
            Referencia justificada
          </label>
          <label className="referral-check-option">
            <input type="checkbox" checked={derivationJustified} onChange={(event) => setDerivationJustified(event.target.checked)} />
            Derivacion justificada
          </label>
          <select value={reverseFlow} onChange={(event) => setReverseFlow(event.target.value)}>
            <option value="contrareferencia">Contrarreferencia</option>
            <option value="referencia_inversa">Referencia inversa</option>
          </select>
        </div>
        <textarea
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Motivo de interconsulta o referencia"
          rows={3}
        />
        <textarea
          value={clinicalSummary}
          onChange={(event) => setClinicalSummary(event.target.value)}
          placeholder="Resumen clinico para el servicio receptor"
          rows={4}
        />
        <textarea
          value={findingsAndTreatment}
          onChange={(event) => setFindingsAndTreatment(event.target.value)}
          placeholder="Hallazgos relevantes, examenes realizados y tratamiento instaurado"
          rows={3}
        />
        <textarea
          value={requestedAction}
          onChange={(event) => setRequestedAction(event.target.value)}
          placeholder="Solicitud especifica: valoracion, manejo, procedimiento, seguimiento"
          rows={2}
        />
        <textarea
          value={administrativeNotes}
          onChange={(event) => setAdministrativeNotes(event.target.value)}
          placeholder="Observaciones administrativas o de coordinacion"
          rows={2}
        />
      </div>

      <button type="button" className="btn btn-primary full-width" onClick={saveReferral} disabled={isSaving}>
        {isSaving ? 'Guardando...' : 'Guardar HCU 053'}
      </button>
      {statusMessage && <p className="clinical-files-message">{statusMessage}</p>}

      {referrals.length > 0 && (
        <div className="prescription-history">
          <strong>Interconsultas y referencias emitidas</strong>
          {referrals.slice(0, 5).map((referral) => (
            <div key={referral.id} className="prescription-history-item referral-history-item">
              <span>
                {new Date(referral.createdAt).toLocaleString('es-EC')} · {referralTypeLabels[referral.referralType]} · {referral.targetSpecialty} · {priorityLabels[referral.priority]} · {statusLabels[referral.status]}
              </span>
              <small>
                Receptor: {referral.targetProvider || 'Especialidad sin medico asignado'} · Motivo: {referral.reason || 'Sin motivo'}
              </small>
              <div className="referral-history-actions">
                <button type="button" onClick={() => openPdf(referral)}>PDF</button>
                <select value={referral.status} onChange={(event) => updateReferralStatus(referral, event.target.value)}>
                  {Object.entries(statusLabels).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default InterconsultationReferralPanel;
